import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';

const SummaryCardContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const ACCORDION_SUMMARY_CARD_REFRESH_STARTED = 'accordion-summary-card-started';
const ACCORDION_SUMMARY_CARD_REFRESH_SUCCEEDED = 'accordion-summary-card-succeeded';
const ACCORDION_SUMMARY_CARD_REFRESH_FAILED = 'accordion-summary-card-refresh-failed';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    accordionSummaryCardCollection: null,
    isAccordionSummaryCardRefreshing: false,
    accordionSummaryCardRefreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        accordionSummaryCardCollection: action.accordionSummaryCardCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case ACCORDION_SUMMARY_CARD_REFRESH_STARTED: {
      return {
        ...state,
        isAccordionSummaryCardRefreshing: true,
        accordionSummaryCardRefreshError: null,
      };
    }
    case ACCORDION_SUMMARY_CARD_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isAccordionSummaryCardRefreshing: false,
        accordionSummaryCardRefreshError: null,
        accordionSummaryCardCollection: action.accordionSummaryCardCollection,
      };
    }
    case ACCORDION_SUMMARY_CARD_REFRESH_FAILED: {
      return {
        ...state,
        isAccordionSummaryCardRefreshing: false,
        accordionSummaryCardRefreshError: action.accordionSummaryCardRefreshError,
      };
    }
    default: {
      throw new Error(`Unhandled SummaryCardContext action type: ${action.type}`);
    }
  }
};

const SummaryCardProvider = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const refreshAccordionSummaryCardCollection = React.useCallback(async () => {
    const { accordionSummaryCardCollection: accordionSummaryCardColl } = state;

    if (accordionSummaryCardColl === null) {
      return;
    }

    if (accordionSummaryCardColl.resources().length() === 0) {
      return;
    }

    const [accordionSummaryCardRes] = accordionSummaryCardColl;

    try {
      dispatch({ type: ACCORDION_SUMMARY_CARD_REFRESH_STARTED });

      const coll = await accordionSummaryCardRes.link('self').get();

      dispatch({ type: ACCORDION_SUMMARY_CARD_REFRESH_SUCCEEDED, accordionSummaryCardCollection: coll });
    } catch (err) {
      dispatch({ type: ACCORDION_SUMMARY_CARD_REFRESH_FAILED, accordionSummaryCardRefreshError: err });
    }
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      accordionSummaryCardCollection: state.accordionSummaryCardCollection,
      isAccordionSummaryCardRefreshing: state.isAccordionSummaryCardRefreshing,
      accordionSummaryCardRefreshError: state.accordionSummaryCardRefreshError,
      refreshAccordionSummaryCardCollection,
    };
  }, [
    state,
    refreshAccordionSummaryCardCollection,
  ]);

  const loadAccordionSummaryCardCollection = () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      const resp = new Error('get whatever it is that you need to get to initialize');

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        accordionSummaryCardCollection: resp,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  };

  useComponentDidMount(loadAccordionSummaryCardCollection);

  return (
    <SummaryCardContext.Provider value={memoizedValue}>
      {children}
    </SummaryCardContext.Provider>
  );
};

// Use SummaryCard State Context
const useSummaryCardContext = () => {
  const context = React.useContext(SummaryCardContext);
  if (context === undefined) {
    throw new Error('useSummaryCardContext must be used within a SummaryCardProvider');
  }
  return context;
};

SummaryCardProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  SummaryCardProvider,
  useSummaryCardContext,
};
